export type Volume = {
  num: string;
  range: string;
  title: string;
  pack: string;
  summary: string;
  sessions: string[];
};

export const VOLUMES: Volume[] = [
  {
    num: '01',
    range: 'Sessions 01–13',
    title: 'Clarity, Values & Goals',
    pack: 'The Kickoff Pack',
    summary: 'Start every client relationship with clarity: where they are now, what matters to them and a 90-day plan they own.',
    sessions: [
      'The Discovery Session: Where Are You Now?',
      'The Wheel of Life Snapshot',
      'Core Values Discovery',
      'Values in Action: Where Life Feels Out of Alignment',
      'Strengths Inventory',
      'The One-Year Vision',
      'From Vision to Three Goals',
      'Sharper Goals: Making Goals Specific and Measurable',
      'Priorities and the Not-Now List',
      'Defining Success on Your Own Terms',
      'Energy Audit: What Fuels You and What Drains You',
      'The 90-Day Plan',
      'Kickoff Agreement and First Milestone Review',
    ],
  },
  {
    num: '02',
    range: 'Sessions 14–26',
    title: 'Mindset & Limiting Beliefs',
    pack: 'The Mindset Pack',
    summary: 'Help clients notice the inner critic, test the stories that hold them back and build real evidence of confidence.',
    sessions: [
      'Catching the Inner Critic',
      'Naming a Limiting Belief',
      'Evidence For and Against',
      'Reframing the Story',
      'Fixed Language to Growth Language',
      'Imposter Feelings at Work',
      'Fear of Failure: Worst, Best and Most Likely',
      'Perfectionism and Good Enough',
      'Comparison and Your Own Lane',
      'Permission Statements',
      'The Self-Talk Rewrite',
      'Borrowed Rules: Shoulds and Musts',
      'The Confidence Evidence Log',
    ],
  },
  {
    num: '03',
    range: 'Sessions 27–39',
    title: 'Emotions, Stress & Resilience',
    pack: 'The Resilience Pack',
    summary: 'Non-clinical sessions to name emotions, spot overload early, recover after setbacks and build a personal resilience toolkit.',
    sessions: [
      'Naming What You Feel',
      'The Emotion Triggers Map',
      'The Stress Signal Check',
      'The Pause Before Reacting',
      'Energy and Recovery Plan',
      'Early Warning Signs of Overload',
      'Worry Time and the Circle of Control',
      'Navigating Change and Uncertainty',
      'The Bounce-Back Review After a Setback',
      'Self-Compassion in Practice',
      'What Is Working: Gratitude and Wins',
      'Building Your Support Network',
      'Your Personal Resilience Toolkit',
    ],
  },
  {
    num: '04',
    range: 'Sessions 40–52',
    title: 'Habits, Action & Accountability',
    pack: 'The Momentum Pack',
    summary: 'Turn insight into action with habit design, procrastination tools, weekly planning and an accountability structure that holds.',
    sessions: [
      'From Insight to Action',
      'Habit Design: The Tiny First Step',
      'Habit Stacking and Cues',
      'Breaking an Unwanted Habit',
      'The Procrastination Decoder',
      'The Time Audit',
      'The Weekly Planning Ritual',
      'Focus Blocks and Distractions',
      'Commitments You Can Actually Keep',
      'Building Your Accountability Structure',
      'When You Miss a Commitment',
      'Motivation Versus Systems',
      'Progress Review and Celebration',
    ],
  },
  {
    num: '05',
    range: 'Sessions 53–65',
    title: 'Communication, Relationships & Boundaries',
    pack: 'The Connection Pack',
    summary: 'Prepare hard conversations, set clear boundaries, give and receive feedback and step out of people-pleasing.',
    sessions: [
      'Your Communication Style',
      'Active Listening Practice',
      'Preparing a Difficult Conversation',
      'Saying No Without Guilt',
      'Setting a Clear Boundary',
      'Clear Requests and Agreements',
      'Giving Feedback That Lands',
      'Receiving Feedback Without Defending',
      'Conflict: Positions Versus Needs',
      'Repairing Trust',
      'People-Pleasing Patterns',
      'The Relationship Map',
      'Assertiveness Rehearsal',
    ],
  },
  {
    num: '06',
    range: 'Sessions 66–78',
    title: 'Leadership, Career & Integration',
    pack: 'The Next Chapter Pack',
    summary: 'Leadership presence, career decisions and transitions, purpose, and a closing session that celebrates the journey.',
    sessions: [
      'Leadership Presence',
      'Decision-Making Under Uncertainty',
      'Delegation and Letting Go',
      'Leading a Team Through Change',
      'The Career Crossroads',
      'The Career Transition Plan',
      'Your Professional Story',
      'Work-Life Integration',
      'Purpose and Contribution',
      'The Letter From Your Future Self',
      'The Year in Review',
      'Sustaining Growth After Coaching',
      'The Closing Session: Celebration and Next Steps',
    ],
  },
];

export const PAINS = [
  { tag: 'SUNDAY NIGHT', title: 'Planning sessions from scratch. Again.', text: 'Your weekend disappears into next week\'s session plans, one client at a time.' },
  { tag: 'OLD NOTES', title: 'That exercise from two years ago', text: 'You know it worked. You just cannot find it in 47 folders and three notebooks.' },
  { tag: 'SOMETHING NEW', title: 'The client brings a topic you did not plan', text: 'A hard conversation, a habit, a career decision, and you are improvising.' },
  { tag: 'WORKSHEETS', title: 'Half-finished handouts', text: 'You keep meaning to design a proper worksheet for that session. It never happens.' },
  { tag: 'GROUPS', title: 'A workshop next week', text: 'Turning a 1:1 exercise into a group or online format takes another evening.' },
  { tag: 'ENERGY', title: 'Prep time eats coaching time', text: 'Hours that could go to clients, marketing or rest go to rebuilding the same material.' },
];

export const INSIDE = [
  { title: 'Goal & best for', text: 'What the session achieves and which client it is right for, so you pick in seconds.' },
  { title: 'Check-in', text: 'Two opening questions to start the session with focus.' },
  { title: 'Core exercise, step by step', text: '4 to 6 clear steps, with the exact coach lines where they help.' },
  { title: 'Powerful questions', text: 'Five open questions to go deeper during the exercise.' },
  { title: 'Group & online version', text: 'How to run the same session with a group or on a video call.' },
  { title: 'Between-session action & close', text: 'A small homework action and a closing question, plus a coach note.' },
];

export const PREVIEW = {
  title: 'SESSION 02 · The Wheel of Life Snapshot',
  goal: 'A scored picture of eight life areas and one area chosen for a small, realistic lift.',
  bestFor: 'A client who says "everything feels a bit off" and needs to see where the imbalance sits.',
  time: '1:1 about 60 minutes · Group about 90 minutes · Client worksheet: Worksheet 02',
  steps: [
    'Introduce eight areas: career, money, health, relationships, family and friends, fun, personal growth, and environment. Let the client rename any that do not fit.',
    'Ask them to score each area\'s satisfaction from 1 to 10, quickly and without overthinking.',
    'For the two highest scores, ask: "What are you doing here that is working?"',
    'For the lowest scores, ask: "What would a 10 look like for you, in your own words?"',
    'Ask the client to choose one area and name what would move it just one point higher this month.',
  ],
  questions: [
    'What surprises you when you see the whole wheel at once?',
    'Where is a high score costing you something elsewhere?',
    'What is the smallest change that would make the biggest ripple?',
  ],
};

export const BONUSES = [
  { short: 'Questions Bank', title: 'The 300 Powerful Coaching Questions Bank™', text: '300 open questions organized by moment of the session, by topic and for special moments (silence, going in circles, breakthroughs).', value: 32 },
  { short: 'Intake Toolkit', title: 'The Client Intake & Discovery Toolkit™', text: 'Intake form, discovery call script, fit checklist, agreement template, session notes and feedback forms.', value: 29 },
  { short: 'Workshop Playbook', title: 'The Group Workshop & Facilitation Playbook™', text: 'Workshop designs for 60, 90 and 180 minutes, icebreakers, energizers, debriefs and online facilitation.', value: 32 },
  { short: '52-Week Planner', title: 'The 52-Week Coaching Planner & Client Progress Tracker™', text: 'Program maps for 12 weeks, 6 months and a full year, plus progress, goals and commitment trackers.', value: 27 },
];

export const STEPS = [
  { n: '1', title: 'Open the PDF', text: 'Instant access after checkout. Read it on any device or print it.' },
  { n: '2', title: 'Pick a session', text: 'Choose by volume and topic, or follow the 52-week planner.' },
  { n: '3', title: 'Coach', text: 'Run it 1:1, in a group or online, and hand your client the matching worksheet.' },
];

export const FAQ = [
  { q: 'Who is this for?', a: 'Life, executive, career, wellness and leadership coaches, and facilitators who run workshops, whether you are new or experienced.' },
  { q: 'Is this a course?', a: 'No. There are no videos to watch. It is a ready-to-use toolkit: open it, pick a session and coach.' },
  { q: 'What exactly do I get?', a: '78 complete session guides and 78 matching client worksheets (156 pieces) in 6 volumes, plus 4 bonuses, as instant PDF downloads.' },
  { q: 'Can I use it with groups and online?', a: 'Yes. Every session includes a group and online version, and Bonus 3 covers workshop facilitation in depth.' },
  { q: 'Can I give the worksheets to my clients?', a: 'Yes. The worksheets are made to be printed, shared on screen or sent to your clients before or after a session.' },
  { q: 'Is it therapy?', a: 'No. These are non-clinical coaching sessions. When a client needs mental health support, refer them to a licensed professional.' },
  { q: 'What if it is not for me?', a: 'You have a 7-day risk-free guarantee. If the toolkit does not fit your coaching, email us within 7 days of purchase and you get your money back.' },
];
